<template>
  <div class="flex flex-col h-screen">
    <answers-header :bot-id="botId"
                    display-last-report-info
    />
    <answers-report-empty-states v-if="displayEmptyStates"/>
    <div v-else class="flex min-h-0 h-full">
      <answers-report-intents class="w-3/12 pb-4"
                              :loading="initialLoading"
                              @intent-selected="handleIntentSelect"
      />

      <shadowed-wrapper class="w-5/12 bg-white">
        <answers-report-examples :key="updateExamples"
                                 :reason-icons="reasonIcons"
                                 :reason-labels="reasonLabels"
                                 :loading="initialLoading"
        />
      </shadowed-wrapper>

      <answers-report-examples-detail :key="updateExamples"
                                      class="w-4/12"
                                      :reason-icons="reasonIcons"
                                      :reason-labels="reasonLabels"
                                      :bot-id="botId"
                                      :loading="initialLoading"
      />
    </div>
  </div>
</template>

<script>
import AnswersHeader from '@/components/answers/AnswersHeader'
import AnswersReportIntents from '@/components/answers/report/AnswersReportIntents'
import AnswersReportExamples from '@/components/answers/report/AnswersReportExamples'
import AnswersReportExamplesDetail from '@/components/answers/report/AnswersReportExamplesDetail'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper'
import AnswersReportEmptyStates from '@/components/answers/report/AnswersReportEmptyStates'
import testReportModule from '@/store/modules/test-report.module'
import { REPORT_STATUSES } from '@/store/constants'
import lowConfidenceIcon from '@/assets/images/icons/low_confidence_icon.svg'
import wrongIntentIcon from '@/assets/images/icons/wrong_intent_icon.svg'
import unknownIntentIcon from '@/assets/images/icons/unknown_intent_icon.svg'
import testReportService from '@/services/amio/test-report.service'
import toaster from '@/utils/toaster'
import botModule from '@/store/modules/bot.module'
import isEmpty from 'ramda/src/isEmpty'

export default {
  name: 'AnswersReport',

  components: {
    AnswersReportExamplesDetail,
    AnswersHeader,
    AnswersReportIntents,
    AnswersReportExamples,
    AnswersReportEmptyStates,
    ShadowedWrapper
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      initialLoading: true,
      reasonLabels: {
        LOW_CONFIDENCE: 'Low confidence',
        WRONG_INTENT: 'Wrong answer',
        UNKNOWN_INTENT: 'Not recognized'
      },
      REPORT_STATUSES,
      updateExamples: 0
    }
  },

  watch: {
    async isBotLoading(newValue) {
      if (!newValue && !isEmpty(this.selectedBot)) {
        await this.loadReport()
        this.initSelectedIntent()
        this.initialLoading = false
      }
    },
    async 'statuses.lastFinishedStatus'() {
      if (!this.initialLoading) await this.loadReport()

      this.initSelectedIntent()
      this.updateExamples = Math.random()
      this.initialLoading = false
    }
  },

  computed: {
    failedIntents: testReportModule.getIntents,
    selectedBot: botModule.getBot,
    isBotLoading: botModule.getBotLoading,
    statuses: testReportModule.getReportStatuses,

    nlpModelId() {
      return botModule.getBot().nlp_model_staging_id
    },

    reasonIcons() {
      return {
        [this.reasonLabels.LOW_CONFIDENCE]: lowConfidenceIcon,
        [this.reasonLabels.WRONG_INTENT]: wrongIntentIcon,
        [this.reasonLabels.UNKNOWN_INTENT]: unknownIntentIcon
      }
    },

    displayEmptyStates() {
      return [REPORT_STATUSES.NEVER_RUN, REPORT_STATUSES.PASSED, REPORT_STATUSES.ERROR].includes(this.statuses.lastFinishedStatus)
    }
  },

  methods: {
    async loadReport() {
      try {
        const report = await testReportService.fetchReport(this.nlpModelId, 'failed,passed,error')
        testReportModule.setReportStatuses({ statusType: 'lastFinishedStatus', status: report.status })
        testReportModule.setIntents(report.failedIntents)
        testReportModule.setReportLastRunDate(report.lastRunDatetime)
      } catch (err) {
        toaster.error(err)
      }
    },

    initSelectedIntent() {
      const { intentId } = this.$route.query
      const selectedIntentId = intentId || this.failedIntents?.[0]?.intent_id
      if (!selectedIntentId) return

      this.handleIntentSelect(selectedIntentId)
    },

    handleIntentSelect(id) {
      if (id !== this.$route.query.intentId) {
        this.$router.replace({ query: { intentId: id } })
      }

      const selectedIntent = this.failedIntents?.find(({ intent_id }) => intent_id === id)
      testReportModule.setSelectedIntent(selectedIntent)
    }
  }
}
</script>
